<template>
  <div>
    <v-container fluid>
      <v-row class="ma-0 my-4 align-center justify-space-between header-row">
        <h1 class="text-h5 font-weight-bold">Facturación de Clínicas</h1>
        <div class="d-flex header-actions">
          <v-btn color="primary" depressed @click="showLoadPayments = true">
            <v-icon left small>mdi-upload</v-icon>Cargar Pagos
          </v-btn>
          <v-btn color="primary" outlined :loading="exportingPayroll" @click="exportPayroll">
            <v-icon left small>mdi-download</v-icon>Exportar Nómina
          </v-btn>
        </div>
      </v-row>

      <v-card outlined class="pa-4 mb-4">
        <div class="d-flex filters-row">
          <v-text-field
            v-model="search"
            class="search-field"
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar n° de factura, RUT, nombre de clínica o ID de atención"
            aria-label="Buscar"
            outlined
            dense
            clearable
            hide-details
            @input="onFilterChange"
          ></v-text-field>
          <v-autocomplete
            v-model="clinicId"
            class="clinic-field"
            :items="clinics"
            item-text="clinicName"
            item-value="clinicId"
            :loading="loadingClinics"
            label="Clínica / Veterinaria"
            placeholder="Todas"
            outlined
            dense
            clearable
            hide-details
            @change="onFilterChange"
          ></v-autocomplete>
        </div>
        <div class="d-flex align-center flex-wrap chips-row mt-4">
          <span class="text-caption muted mr-1">Estado financiero:</span>
          <button type="button" class="status-chip" :aria-pressed="String(!statuses.length)" :style="allChipStyle" @click="clearStatuses">
            Todos
          </button>
          <button
            v-for="key in filterStatuses"
            :key="key"
            type="button"
            class="status-chip"
            :aria-pressed="String(statuses.includes(key))"
            :style="chipStyle(key)"
            @click="toggleStatus(key)"
          >
            {{ statusMeta[key].label }}
          </button>
        </div>
      </v-card>

      <v-card outlined>
        <v-data-table
          :headers="headers"
          :items="rows"
          item-key="rowKey"
          :loading="loading"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
          no-data-text="No hay facturas que coincidan con la búsqueda o los filtros seleccionados."
        >
          <template v-slot:item.status="{ item }">
            <settlement-status-badge :status="item.status"></settlement-status-badge>
          </template>
          <template v-slot:item.clinicName="{ item }">
            <a class="clinic-link" @click="openDetail(item)">{{ item.clinicName }}</a>
          </template>
          <template v-slot:item.clinicRut="{ item }">
            {{ formatRut(item.clinicRut) }}
          </template>
          <template v-slot:item.invoiceNumber="{ item }">
            <a class="invoice-link" @click="openDetail(item)">{{ invoiceLabel(item.invoiceNumber) }}</a>
          </template>
          <template v-slot:item.invoiceIssuedAt="{ item }">
            {{ formatDate(item.invoiceIssuedAt) || 'Pendiente' }}
          </template>
          <template v-slot:item.cutoffDate="{ item }">
            {{ formatDate(item.cutoffDate) }}
          </template>
          <template v-slot:item.attendances="{ item }">
            {{ attendancesLabel(item.attendances) }}
          </template>
          <template v-slot:item.pawerAmount="{ item }">
            <span class="amount" :style="amountStyle(item)">{{ formatClp(item.pawerAmount) }}</span>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <clinic-settlement-detail-dialog
      v-model="showDetail"
      :clinic-id="selected.clinicId"
      :cutoff-date="selected.cutoffDate"
      @changed="fetchSettlements"
    ></clinic-settlement-detail-dialog>

    <load-payments-dialog v-model="showLoadPayments" @applied="fetchSettlements"></load-payments-dialog>

    <v-dialog v-model="showExcluded" max-width="560">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">Liquidaciones excluidas de la nómina</v-card-title>
        <v-card-text>
          <p class="text-body-2">
            Estas liquidaciones en Pendiente de Pago no se incluyeron en el archivo descargado:
          </p>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Clínica</th>
                <th>N° Factura</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in excluded" :key="index">
                <td>{{ row.clinicName }}</td>
                <td>{{ invoiceLabel(row.invoiceNumber) }}</td>
                <td>{{ row.reason }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="showExcluded = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="5000">{{ snackbarMessage }}</v-snackbar>
  </div>
</template>

<script>
import ClinicSettlementsService from '@/services/clinic-settlements.service';
import ClinicsService from '@/services/clinics.service';
import RutUtils from '@/utils/rut.utils';
import SettlementStatusBadge from '@/components/clinic-billing/SettlementStatusBadge.vue';
import ClinicSettlementDetailDialog from '@/components/clinic-billing/ClinicSettlementDetailDialog.vue';
import LoadPaymentsDialog from '@/components/clinic-billing/LoadPaymentsDialog.vue';
import { utilsMixin } from '@/mixins/utilsMixin';
import {
  STATUS_META,
  FILTER_STATUSES,
  attendancesLabel,
  downloadTextFile,
  formatClp,
  formatDate,
  invoiceLabel
} from '@/utils/clinic-settlements.utils';

const SEARCH_DEBOUNCE_MS = 400;

// "Finanzas › Facturación de Clínicas" (pawer-backend specs/125-clinic-billing-admin).
// Una fila por liquidación (clínica × corte), paginada en el servidor y ordenada por el
// backend (fecha de corte descendente, luego clínica), por eso las columnas no ordenan.
export default {
  name: 'ClinicBilling',
  components: { SettlementStatusBadge, ClinicSettlementDetailDialog, LoadPaymentsDialog },
  mixins: [utilsMixin],
  data() {
    return {
      headers: [
        { text: 'Estado', value: 'status', sortable: false },
        { text: 'Clínica', value: 'clinicName', sortable: false },
        { text: 'RUT', value: 'clinicRut', sortable: false },
        { text: 'N° Factura', value: 'invoiceNumber', sortable: false },
        { text: 'Fecha de emisión', value: 'invoiceIssuedAt', sortable: false },
        { text: 'Fecha de corte', value: 'cutoffDate', sortable: false },
        { text: 'Atenciones', value: 'attendances', sortable: false },
        { text: 'Monto Pawer', value: 'pawerAmount', sortable: false, align: 'end' }
      ],
      statusMeta: STATUS_META,
      filterStatuses: FILTER_STATUSES,
      rows: [],
      total: 0,
      loading: false,
      search: '',
      clinicId: null,
      statuses: [],
      clinics: [],
      loadingClinics: false,
      searchTimer: null,
      options: { page: 1, itemsPerPage: 20 },
      showDetail: false,
      selected: { clinicId: '', cutoffDate: '' },
      showLoadPayments: false,
      exportingPayroll: false,
      showExcluded: false,
      excluded: [],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    allChipStyle() {
      const active = !this.statuses.length;
      return {
        background: active ? 'var(--pw-primary)' : 'var(--pw-surface)',
        color: active ? 'var(--pw-surface)' : 'var(--pw-text)',
        borderColor: active ? 'var(--pw-primary)' : 'var(--pw-border-strong)'
      };
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchSettlements();
      },
      deep: true
    }
  },
  created() {
    this.loadClinics();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    formatRut(rut) {
      return RutUtils.formatIdentifier(rut);
    },
    formatClp,
    formatDate,
    attendancesLabel,
    invoiceLabel,
    chipStyle(key) {
      const meta = STATUS_META[key];
      const active = this.statuses.includes(key);
      return { background: active ? meta.bg : 'var(--pw-surface)', color: meta.fg, borderColor: meta.dot };
    },
    amountStyle(item) {
      return item.status === 'credit_note_required' ? { color: STATUS_META.credit_note_required.strong } : {};
    },
    async loadClinics() {
      this.loadingClinics = true;
      const res = await ClinicsService.getDroopyClinics();
      if (res.status === 'ok') this.clinics = res.data || [];
      else this.showErrorSnackbar('No se pudieron cargar las clínicas.');
      this.loadingClinics = false;
    },
    buildSearchPayload() {
      return {
        page: this.options.page,
        pageSize: this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 100,
        search: (this.search || '').trim(),
        clinicId: this.clinicId || null,
        statuses: [...this.statuses]
      };
    },
    async fetchSettlements() {
      this.loading = true;
      const res = await ClinicSettlementsService.search(this.buildSearchPayload());
      this.loading = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar('No se pudieron cargar las liquidaciones.');
        return;
      }
      this.rows = (res.data || []).map((row) => ({ ...row, rowKey: `${row.clinicId}|${row.cutoffDate}` }));
      this.total = (res.meta && res.meta.total) || 0;
    },
    onFilterChange() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reloadFromFirstPage(), SEARCH_DEBOUNCE_MS);
    },
    reloadFromFirstPage() {
      if (this.options.page === 1) this.fetchSettlements();
      else this.options = { ...this.options, page: 1 };
    },
    toggleStatus(key) {
      this.statuses = this.statuses.includes(key)
        ? this.statuses.filter((status) => status !== key)
        : [...this.statuses, key];
      this.onFilterChange();
    },
    clearStatuses() {
      if (!this.statuses.length) return;
      this.statuses = [];
      this.onFilterChange();
    },
    openDetail(item) {
      this.selected = { clinicId: item.clinicId, cutoffDate: item.cutoffDate };
      this.showDetail = true;
    },
    async exportPayroll() {
      this.exportingPayroll = true;
      const res = await ClinicSettlementsService.getPayroll();
      this.exportingPayroll = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar('No se pudo generar la nómina. Intenta nuevamente.');
        return;
      }
      const { filename, content, count, excluded } = res.data;
      this.excluded = excluded || [];
      if (!count) {
        this.showWarningSnackbar(
          this.excluded.length
            ? 'Ninguna liquidación en Pendiente de Pago se pudo incluir en la nómina.'
            : 'No hay liquidaciones en Pendiente de Pago para incluir en la nómina.'
        );
      } else {
        downloadTextFile(content, filename || 'nomina-clinicas.csv');
        this.showSuccessSnackbar(count === 1 ? 'Nómina descargada con 1 liquidación.' : `Nómina descargada con ${count} liquidaciones.`);
      }
      this.showExcluded = this.excluded.length > 0;
    }
  }
};
</script>

<style scoped>
.header-row,
.header-actions,
.filters-row,
.chips-row {
  gap: 10px;
}
.filters-row {
  gap: 12px;
}
.search-field {
  flex-grow: 1;
}
.clinic-field {
  max-width: 300px;
}
.status-chip {
  height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.clinic-link {
  font-weight: 600;
  color: var(--pw-text);
}
.invoice-link {
  font-weight: 600;
  text-decoration: underline;
}
.amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.muted {
  color: var(--pw-text-muted);
}
</style>
